import { useState } from "react";
import { Link } from "react-router";
import { IconPackage, IconDotsVertical, IconTrash } from "@tabler/icons-react";
import { formatCreatedAt } from "@/lib/utils";
import type { DfyOrderListResponseDto } from "@repo/api/dfy";
import { getDfyOrderName } from "@repo/api/utils/dfy-order";
import DfyOrderStatusBadge from "./DfyOrderStatusBadge";
import { Button } from "@/lib/components/Button/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/lib/components/DropdownMenu";
import { useUnseenNotificationsQuery } from "@/lib/queries/notification-queries";

export { dfyStatusLabel } from "./DfyOrderStatusBadge";

type Order = DfyOrderListResponseDto["orders"][number];

export default function DfyOrdersList({
  orders,
  orderHref,
  onDeleteDraft,
}: {
  orders: Order[];
  orderHref: (order: Order) => string;
  onDeleteDraft?: (externalId: string) => void;
}) {
  const { data: unseen = [] } = useUnseenNotificationsQuery();
  const unseenOrderIds = new Set(
    unseen.flatMap((notification) =>
      notification.dfyOrder ? [notification.dfyOrder.externalId] : [],
    ),
  );

  return (
    <ul className="grid w-full animate-fade-in grid-cols-1 gap-x-6 gap-y-8 p-6 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
      {orders.map((order) => (
        <li key={order.externalId} className="min-w-0">
          <OrderCard
            order={order}
            href={orderHref(order)}
            onDeleteDraft={onDeleteDraft}
            hasUnseenNotification={unseenOrderIds.has(order.externalId)}
          />
        </li>
      ))}
    </ul>
  );
}

export function OrderCard({
  order,
  href,
  onDeleteDraft,
  hasUnseenNotification = false,
}: {
  order: Order;
  href: string;
  onDeleteDraft?: (externalId: string) => void;
  hasUnseenNotification?: boolean;
}) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const showDelete = order.status === "DRAFT" && Boolean(onDeleteDraft);

  return (
    <Link
      to={href}
      title={`Order #${order.externalId}`}
      className={`group flex min-w-0 flex-col rounded-t-xl text-text-primary outline-offset-4 transition-transform duration-200 hover:scale-[1.02] focus-visible:outline-2 focus-visible:outline-tertiary-400 motion-reduce:transform-none motion-reduce:transition-none ${
        menuOpen ? "scale-[1.02]" : ""
      }`}
    >
      <div className="relative aspect-video self-stretch overflow-hidden rounded-xl bg-gray-100">
        {order.thumbnailSrc && failedSrc !== order.thumbnailSrc ? (
          <img
            src={order.thumbnailSrc}
            alt=""
            loading="lazy"
            decoding="async"
            onError={() => setFailedSrc(order.thumbnailSrc ?? null)}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <IconPackage
              size={48}
              className="text-text-secondary"
              aria-hidden="true"
            />
          </div>
        )}
        <DfyOrderStatusBadge status={order.status} variant="image" />
        {showDelete && (
          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="iconXs"
                className="absolute top-2 right-2 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 pointer-coarse:opacity-100"
                aria-label="Order options"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
              >
                <IconDotsVertical />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              onClick={(event) => event.stopPropagation()}
            >
              <DropdownMenuItem
                className="text-error focus:text-error"
                onSelect={() => onDeleteDraft?.(order.externalId)}
              >
                <IconTrash size={16} />
                Delete draft
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <div className="mt-2">
        <div className="flex items-center gap-2">
          {hasUnseenNotification && (
            <span
              aria-label="New update"
              className="h-2.5 w-2.5 shrink-0 rounded-full border border-white bg-linear-to-br from-primary-400 to-secondary-400"
            />
          )}
          <h2 className="line-clamp-1 flex-1 font-semibold">
            {getDfyOrderName(order.externalId)}
          </h2>
        </div>
        <p className="line-clamp-1 text-sm text-text-secondary">
          {order.submittedAt
            ? formatCreatedAt(order.submittedAt, "Submitted")
            : formatCreatedAt(order.createdAt, "Created")}
        </p>
      </div>
    </Link>
  );
}
